import { useEffect, useMemo, useState } from "react";
import { api, gbp, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, Search, Fuel, AlertOctagon, AlertTriangle, Download, Pencil, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const UNITS = [
  { v: "per_job",    label: "per job" },
  { v: "per_day",    label: "per day" },
  { v: "per_driver", label: "per driver" },
  { v: "per_hour",   label: "per hour" },
  { v: "per_week",   label: "per week" },
  { v: "per_mile",   label: "per mile" },
];
const unitLabel = (u) => UNITS.find(x => x.v === u)?.label || u;

const emptyForm = () => ({
  client_id: "",
  site_id: "",
  label: "",
  charge_rate: "",
  unit: "per_day",
  fuel_included: false,
  fuel_per_day: "",
  fines_included: false,
  fines_lines: [],   // [{reason, amount}]
  notes: "",
});

export default function FinanceChargeable() {
  const [groups, setGroups] = useState([]);     // [{ client_id, client_name, rates: [...] }]
  const [warehousing, setWarehousing] = useState([]); // [{ client_id, client_name, rates: [...] }]
  const [clients, setClients] = useState([]);
  const [allSites, setAllSites] = useState([]); // [{id, client_id, name}]
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = add mode, otherwise rate id
  const [form, setForm] = useState(emptyForm());

  // Warehousing dialog state
  const [whOpen, setWhOpen] = useState(false);
  const [whEditingId, setWhEditingId] = useState(null);
  const [whForm, setWhForm] = useState({ client_id: "", label: "", charge_rate: "", unit: "per_pallet_per_week", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [cr, wh, cl] = await Promise.all([
        api.get("/finance/chargeable-rates"),
        api.get("/finance/warehousing-rates"),
        api.get("/clients"),
      ]);
      setGroups(cr.data.clients || []);
      setWarehousing(wh.data.clients || []);
      setClients(cl.data || []);
      const sitesArrays = await Promise.all(
        (cl.data || []).map(c => api.get(`/clients/${c.id}/sites`).then(r => r.data.map(s => ({ ...s, client_id: c.id }))).catch(() => []))
      );
      setAllSites(sitesArrays.flat());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Fast lookup: client_id -> warehousing rates
  const warehousingByClient = useMemo(() => {
    const m = {};
    for (const g of warehousing) m[g.client_id] = g.rates || [];
    return m;
  }, [warehousing]);

  // Sites that have no charge rate yet — surfaced as a warning banner so ops
  // can price them before month-end. We build a Set of "rated" site ids from
  // the API response (which is grouped by client) and diff against allSites.
  const unpricedSites = useMemo(() => {
    const rated = new Set();
    for (const g of groups) {
      for (const r of (g.rates || [])) {
        if (r.site_id) rated.add(r.site_id);
      }
    }
    return allSites
      .filter((s) => !rated.has(s.id))
      .map((s) => {
        const cl = clients.find((c) => c.id === s.client_id) || {};
        return { client_id: s.client_id, client_name: cl.name || "—", site_id: s.id, site_name: s.name || "—" };
      })
      .sort((a, b) => (a.client_name || "").localeCompare(b.client_name || "") || (a.site_name || "").localeCompare(b.site_name || ""));
  }, [allSites, groups, clients]);
  const unpricedByClient = useMemo(() => {
    const m = {};
    for (const u of unpricedSites) {
      m[u.client_id] = (m[u.client_id] || 0) + 1;
    }
    return m;
  }, [unpricedSites]);

  const openAddWarehousing = (clientId) => {
    setWhEditingId(null);
    setWhForm({ client_id: clientId, label: "", charge_rate: "", unit: "per_pallet_per_week", notes: "" });
    setWhOpen(true);
  };
  const openEditWarehousing = (clientId, r) => {
    setWhEditingId(r.id);
    setWhForm({ client_id: clientId, label: r.label || "", charge_rate: String(r.charge_rate ?? ""), unit: r.unit || "per_pallet_per_week", notes: r.notes || "" });
    setWhOpen(true);
  };
  const saveWarehousing = async () => {
    if (!whForm.client_id) { toast.error("Pick a client first"); return; }
    if (Number(whForm.charge_rate) <= 0) { toast.error("Charge rate must be > 0"); return; }
    try {
      const payload = {
        label: whForm.label.trim(),
        charge_rate: Number(whForm.charge_rate),
        unit: whForm.unit,
        notes: whForm.notes,
      };
      if (whEditingId) {
        await api.put(`/warehousing-rates/${whEditingId}`, payload);
        toast.success("Warehousing rate updated");
      } else {
        await api.post(`/clients/${whForm.client_id}/warehousing-rates`, payload);
        toast.success("Warehousing rate added");
      }
      setWhOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteWarehousing = async (id) => {
    if (!window.confirm("Delete this warehousing rate?")) return;
    try {
      await api.delete(`/warehousing-rates/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sitesOfSelectedClient = useMemo(
    () => allSites.filter(s => s.client_id === form.client_id),
    [allSites, form.client_id]
  );

  // Merged view: every client that has at least one site (or an existing rate)
  // gets a group. Each group lists all sites — those without a rate show a
  // "+ Add rate" cell so the ops team just fills prices instead of picking sites.
  const mergedGroups = useMemo(() => {
    const byId = new Map();
    // Seed with clients that have sites (so brand-new clients show up too).
    for (const cl of clients) {
      byId.set(cl.id, { client_id: cl.id, client_name: cl.name || "—", rates: [], sites: [] });
    }
    // Attach sites in name order.
    for (const s of allSites) {
      const g = byId.get(s.client_id);
      if (g) g.sites.push({ id: s.id, name: s.name });
    }
    for (const g of byId.values()) {
      g.sites.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    // Overlay existing rates from the API response.
    for (const g of groups) {
      const gm = byId.get(g.client_id);
      if (gm) {
        gm.rates = g.rates || [];
      } else {
        // Client no longer in the clients list but still has rates — keep it visible.
        byId.set(g.client_id, { ...g, sites: (g.rates || []).map(r => ({ id: r.site_id, name: r.site_name })) });
      }
    }
    // Sort clients by name.
    const out = Array.from(byId.values());
    out.sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""));
    // Build the row list per group: one row per site (or per stray rate without a
    // matching site), with the rate attached where it exists.
    for (const g of out) {
      const rateBySite = new Map();
      for (const r of g.rates) {
        if (!rateBySite.has(r.site_id)) rateBySite.set(r.site_id, []);
        rateBySite.get(r.site_id).push(r);
      }
      const rows = [];
      for (const s of g.sites) {
        const rs = rateBySite.get(s.id) || [{ __placeholder: true, site_id: s.id, site_name: s.name }];
        for (const r of rs) rows.push({ ...r, site_id: s.id, site_name: s.name });
        rateBySite.delete(s.id);
      }
      // Any remaining rates whose site was deleted — still show them.
      for (const rs of rateBySite.values()) for (const r of rs) rows.push(r);
      g.rows = rows;
    }
    return out;
  }, [clients, allSites, groups]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mergedGroups;
    return mergedGroups.filter(g =>
      (g.client_name || "").toLowerCase().includes(q) ||
      (g.rows || []).some(r =>
        (r.site_name || "").toLowerCase().includes(q) ||
        (r.label || "").toLowerCase().includes(q),
      ),
    );
  }, [mergedGroups, search]);

  // Collapsed by default — click the chevron next to the client name to expand.
  // While a search filter is active every group auto-opens so results are visible.
  const [expanded, setExpanded] = useState({});
  const isOpen = (cid) => {
    if (search.trim()) return true;
    return expanded[cid] ?? false;
  };
  const toggle = (cid) => setExpanded((p) => ({ ...p, [cid]: !isOpen(cid) }));

  const openAdd = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };

  // Pre-fill the dialog when the user taps "+ Add rate" straight from a site row.
  const openAddForSite = (clientId, siteId) => {
    setEditingId(null);
    setForm({ ...emptyForm(), client_id: clientId, site_id: siteId });
    setOpen(true);
  };

  const openEdit = (clientId, r) => {
    setEditingId(r.id);
    setForm({
      client_id: clientId,
      site_id: r.site_id,
      label: r.label || "",
      charge_rate: String(r.charge_rate ?? ""),
      unit: r.unit || "per_day",
      fuel_included: !!r.fuel_included,
      fuel_per_day: String(r.fuel_per_day ?? ""),
      fines_included: !!r.fines_included,
      fines_lines: Array.isArray(r.fines_lines) ? r.fines_lines.map(l => ({ reason: l.reason || "", amount: String(l.amount ?? "") })) : [],
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.client_id) return toast.error("Pick a client");
    if (!form.site_id)   return toast.error("Pick a site");
    if (!form.charge_rate) return toast.error("Enter a charge rate");
    const payload = {
      label: form.label || "",
      charge_rate: parseFloat(form.charge_rate),
      unit: form.unit,
      fuel_included: !!form.fuel_included,
      fuel_per_day: form.fuel_included ? parseFloat(form.fuel_per_day || 0) : 0,
      fines_included: !!form.fines_included,
      fines_lines: form.fines_included
        ? form.fines_lines
            .filter(l => (l.reason || "").trim() || l.amount)
            .map(l => ({ reason: (l.reason || "").trim(), amount: parseFloat(l.amount || 0) }))
        : [],
      notes: form.notes || "",
    };
    try {
      if (editingId) {
        await api.put(`/charge-rates/${editingId}`, payload);
        toast.success("Charge rate updated");
      } else {
        await api.post(`/sites/${form.site_id}/charge-rates`, payload);
        toast.success("Charge rate added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addFineLine = () => setForm(f => ({ ...f, fines_lines: [...f.fines_lines, { reason: "", amount: "" }] }));
  const removeFineLine = (idx) => setForm(f => ({ ...f, fines_lines: f.fines_lines.filter((_, i) => i !== idx) }));
  const updateFineLine = (idx, patch) => setForm(f => ({
    ...f,
    fines_lines: f.fines_lines.map((l, i) => i === idx ? { ...l, ...patch } : l),
  }));

  const deleteRate = async (rate_id) => {
    if (!window.confirm("Delete this charge rate?")) return;
    try {
      await api.delete(`/charge-rates/${rate_id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="finance-chargeable-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold">Chargeable</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Charge rates per site</h1>
          <p className="text-sm text-slate-600 mt-1">Reference card — how much you charge each client for each site. For guidance only.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="add-charge-rate-btn"
            onClick={openAdd}
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add charge rate
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="chargeable-search"
            placeholder="Search by client, site or label"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm border-slate-300"
          />
        </div>
      </Card>

      {/* Warning banner — sites with no charge rate. Nudges ops to price
          them before month-end so job reports don't roll up as £0.00. */}
      {!loading && unpricedSites.length > 0 && (
        <Card
          data-testid="unpriced-sites-banner"
          className="rounded-sm border border-amber-300 bg-amber-50 shadow-none p-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                {unpricedSites.length} site{unpricedSites.length === 1 ? "" : "s"} without a charge rate
              </div>
              <div className="text-xs text-amber-800/90 mt-0.5">
                Price these before month-end so DSP job reports pick them up. Click a site to add its rate.
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {unpricedSites.slice(0, 20).map((u) => (
                  <button
                    key={u.site_id}
                    type="button"
                    data-testid={`unpriced-${u.site_id}`}
                    onClick={() => {
                      setEditingId(null);
                      setForm({
                        client_id: u.client_id,
                        site_id: u.site_id,
                        label: "",
                        charge_rate: "",
                        unit: "per_day",
                        fuel_included: false,
                        fuel_per_day: "",
                        fines_included: false,
                        fines_lines: [],
                        notes: "",
                      });
                      setOpen(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-sm bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                  >
                    <span className="opacity-70">{u.client_name}</span>
                    <span>·</span>
                    <span className="font-semibold">{u.site_name}</span>
                  </button>
                ))}
                {unpricedSites.length > 20 && (
                  <span className="inline-flex items-center text-[11px] text-amber-800/70 px-1.5">
                    +{unpricedSites.length - 20} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && filteredGroups.length === 0 && (
        <Card className="rounded-sm border border-dashed border-slate-300 shadow-none p-12 text-center">
          <div className="text-sm text-slate-700 font-medium">No clients yet.</div>
          <div className="text-xs text-slate-500 mt-1">Add clients and sites first, then their sites will show up here ready to price.</div>
        </Card>
      )}

      {filteredGroups.map(g => {
        const open = isOpen(g.client_id);
        const ratedCount = (g.rates || []).length;
        const siteCount = (g.sites || []).length;
        return (
          <Card key={g.client_id} className="rounded-sm border border-slate-200 shadow-none" data-testid={`client-card-${g.client_id}`}>
            <button
              type="button"
              onClick={() => toggle(g.client_id)}
              data-testid={`client-toggle-${g.client_id}`}
              className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50/60 transition text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                {open
                  ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Client</div>
                  <div className="text-sm font-medium text-slate-900 truncate">{g.client_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                {(unpricedByClient[g.client_id] || 0) > 0 && (
                  <Badge
                    variant="secondary"
                    data-testid={`client-unpriced-${g.client_id}`}
                    className="rounded-sm bg-amber-100 text-amber-800 border-amber-200"
                  >
                    {unpricedByClient[g.client_id]} unpriced
                  </Badge>
                )}
                <Badge variant="secondary" className="rounded-sm" data-testid={`client-badge-${g.client_id}`}>
                  {ratedCount} of {siteCount} priced
                </Badge>
                {g.client_id && (
                  <a
                    data-testid={`download-pdf-${g.client_id}`}
                    href={fileUrl(`/finance/chargeable/${g.client_id}/pdf`)}
                    download
                    rel="noopener"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                  </a>
                )}
              </div>
            </button>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-2.5">Site</th>
                      <th className="text-left px-5 py-2.5">Label</th>
                      <th className="text-right px-5 py-2.5">Charge rate</th>
                      <th className="text-right px-5 py-2.5">Fuel</th>
                      <th className="text-right px-5 py-2.5">Fines</th>
                      <th className="text-left px-5 py-2.5">Notes</th>
                      <th className="text-right px-5 py-2.5 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-slate-500 py-8 text-xs">
                          No sites for this client yet. Add sites on the client&apos;s page first.
                        </td>
                      </tr>
                    )}
                    {g.rows.map((r, idx) => (
                      r.__placeholder ? (
                        <tr key={`ph-${g.client_id}-${r.site_id}-${idx}`} className="border-t border-slate-100 bg-orange-50/30" data-testid={`charge-rate-empty-${r.site_id}`}>
                          <td className="px-5 py-2.5 font-medium">{r.site_name}</td>
                          <td className="px-5 py-2.5" colSpan={5}>
                            <span className="text-xs text-slate-500 italic">Not priced yet</span>
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`charge-rate-add-${r.site_id}`}
                              onClick={() => openAddForSite(g.client_id, r.site_id)}
                              className="h-7 rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50 text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Add rate
                            </Button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={r.id} className="border-t border-slate-100" data-testid={`charge-rate-row-${r.id}`}>
                          <td className="px-5 py-2.5 font-medium">{r.site_name}</td>
                          <td className="px-5 py-2.5 text-slate-700">{r.label || "—"}</td>
                          <td className="px-5 py-2.5 text-right tabular-nums font-semibold">
                            {gbp(r.charge_rate)} <span className="text-xs text-slate-500 font-normal">/ {unitLabel(r.unit)}</span>
                          </td>
                          <td className="px-5 py-2.5 text-right tabular-nums">
                            {r.fuel_included ? (
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <Fuel className="w-3.5 h-3.5" />
                                {gbp(r.fuel_per_day || 0)}<span className="text-xs text-slate-500 font-normal">/day</span>
                              </span>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-5 py-2.5 text-right tabular-nums">
                            {r.fines_included ? (
                              <div className="inline-flex flex-col items-end gap-0.5">
                                {(r.fines_lines || []).length === 0 ? (
                                  <span className="text-amber-700 text-xs">✓ included</span>
                                ) : (r.fines_lines || []).map((ln, i) => (
                                  <div key={i} className="flex items-center gap-1.5 justify-end">
                                    <AlertOctagon className="w-3 h-3 text-red-600" />
                                    <span className="text-red-600 font-medium">{gbp(ln.amount || 0)}</span>
                                    {ln.reason && <span className="text-[11px] text-slate-500 max-w-[180px] truncate" title={ln.reason}>· {ln.reason}</span>}
                                  </div>
                                ))}
                              </div>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-5 py-2.5 text-slate-600">{r.notes || "—"}</td>
                          <td className="px-5 py-2.5 text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button
                                variant="ghost" size="icon"
                                data-testid={`charge-rate-edit-${r.id}`}
                                onClick={() => openEdit(g.client_id, r)}
                                className="h-8 w-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                              ><Pencil className="w-4 h-4" /></Button>
                              <Button
                                variant="ghost" size="icon"
                                data-testid={`charge-rate-delete-${r.id}`}
                                onClick={() => deleteRate(r.id)}
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              ><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {open && null}
            {/* Warehousing sub-block — always visible when the client card is expanded */}
            {isOpen(g.client_id) && (
              <div className="border-t-2 border-slate-100 bg-slate-50/40 px-5 py-3" data-testid={`warehousing-${g.client_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                    Warehousing prices
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openAddWarehousing(g.client_id)}
                    data-testid={`warehousing-add-${g.client_id}`}
                    className="h-7 rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add warehousing rate
                  </Button>
                </div>
                {(warehousingByClient[g.client_id] || []).length === 0 ? (
                  <div className="text-xs text-slate-500 italic py-2">No warehousing rates set.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white text-slate-600 uppercase text-[10.5px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Label</th>
                          <th className="text-right px-3 py-2">Rate</th>
                          <th className="text-left px-3 py-2">Notes</th>
                          <th className="text-right px-3 py-2 w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(warehousingByClient[g.client_id] || []).map((r) => (
                          <tr key={r.id} className="border-t border-slate-200" data-testid={`warehousing-row-${r.id}`}>
                            <td className="px-3 py-2 font-medium">{r.label || "—"}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">
                              {gbp(r.charge_rate)} <span className="text-xs text-slate-500 font-normal">/ {warehousingUnitLabel(r.unit)}</span>
                            </td>
                            <td className="px-3 py-2 text-slate-600">{r.notes || "—"}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex items-center gap-1">
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                  data-testid={`warehousing-edit-${r.id}`}
                                  onClick={() => openEditWarehousing(g.client_id, r)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`warehousing-delete-${r.id}`}
                                  onClick={() => deleteWarehousing(r.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}

      <p className="text-[11px] text-slate-500">For guidance only — these rates are reference info for your team, not used in invoice totals.</p>

      {/* Add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-sm" data-testid="add-charge-rate-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit charge rate" : "Add charge rate"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm(f => ({ ...f, client_id: v, site_id: "" }))} disabled={!!editingId}>
                  <SelectTrigger data-testid="dlg-client" className="rounded-sm border-slate-300 mt-1"><SelectValue placeholder="Pick client" /></SelectTrigger>
                  <SelectContent>
                    {clients.length === 0
                      ? <SelectItem value="__none" disabled>No clients</SelectItem>
                      : clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Site</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm(f => ({ ...f, site_id: v }))} disabled={!form.client_id || !!editingId}>
                  <SelectTrigger data-testid="dlg-site" className="rounded-sm border-slate-300 mt-1"><SelectValue placeholder={form.client_id ? "Pick site" : "Pick client first"} /></SelectTrigger>
                  <SelectContent>
                    {sitesOfSelectedClient.length === 0
                      ? <SelectItem value="__none" disabled>No sites for this client</SelectItem>
                      : sitesOfSelectedClient.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-600">Label (optional)</Label>
                <Input
                  data-testid="dlg-label"
                  value={form.label}
                  onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                  className="rounded-sm border-slate-300 mt-1"
                  placeholder="e.g. Standard, Saturday, Holiday"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Charge rate (£)</Label>
                <Input
                  data-testid="dlg-rate"
                  type="number" min="0" step="0.01"
                  value={form.charge_rate}
                  onChange={(e) => setForm(f => ({ ...f, charge_rate: e.target.value }))}
                  className="rounded-sm border-slate-300 mt-1"
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm(f => ({ ...f, unit: v }))}>
                  <SelectTrigger data-testid="dlg-unit" className="rounded-sm border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u.v} value={u.v}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-sm border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm flex items-center gap-1.5"><Fuel className="w-4 h-4 text-amber-700" /> Fuel included?</Label>
                <Switch
                  data-testid="dlg-fuel-toggle"
                  checked={form.fuel_included}
                  onCheckedChange={(v) => setForm(f => ({ ...f, fuel_included: v }))}
                />
              </div>
              {form.fuel_included && (
                <div className="mt-3">
                  <Label className="text-xs text-slate-600">Fuel allowance £ per day</Label>
                  <Input
                    data-testid="dlg-fuel-amount"
                    type="number" min="0" step="0.01"
                    value={form.fuel_per_day}
                    onChange={(e) => setForm(f => ({ ...f, fuel_per_day: e.target.value }))}
                    className="rounded-sm border-slate-300 mt-1"
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>

            <div className="rounded-sm border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm flex items-center gap-1.5"><AlertOctagon className="w-4 h-4 text-red-600" /> Fines included?</Label>
                <Switch
                  data-testid="dlg-fines-toggle"
                  checked={form.fines_included}
                  onCheckedChange={(v) => setForm(f => ({ ...f, fines_included: v, fines_lines: v && f.fines_lines.length === 0 ? [{ reason: "", amount: "" }] : f.fines_lines }))}
                />
              </div>
              {form.fines_included && (
                <div className="mt-3 space-y-2">
                  {form.fines_lines.length === 0 && (
                    <div className="text-[11px] text-slate-500">No fine lines yet — add one below.</div>
                  )}
                  {form.fines_lines.map((ln, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-7">
                        {idx === 0 && <Label className="text-[11px] text-slate-600">Reason</Label>}
                        <Input
                          data-testid={`dlg-fine-reason-${idx}`}
                          value={ln.reason}
                          onChange={(e) => updateFineLine(idx, { reason: e.target.value })}
                          className="rounded-sm border-slate-300 mt-1 h-9"
                          placeholder="e.g. Late delivery, Missed scan, Damage"
                        />
                      </div>
                      <div className="col-span-4">
                        {idx === 0 && <Label className="text-[11px] text-slate-600">Amount £</Label>}
                        <Input
                          data-testid={`dlg-fine-amount-${idx}`}
                          type="number" min="0" step="0.01"
                          value={ln.amount}
                          onChange={(e) => updateFineLine(idx, { amount: e.target.value })}
                          className="rounded-sm border-slate-300 mt-1 h-9"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          variant="ghost" size="icon"
                          data-testid={`dlg-fine-remove-${idx}`}
                          onClick={() => removeFineLine(idx)}
                          className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                        ><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addFineLine}
                    data-testid="dlg-fine-add-line"
                    className="rounded-sm h-8 text-xs"
                  ><Plus className="w-3.5 h-3.5 mr-1" /> Add fine line</Button>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-slate-600">Notes</Label>
              <Input
                data-testid="dlg-notes"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="rounded-sm border-slate-300 mt-1"
                placeholder="optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} data-testid="dlg-submit" className="rounded-sm bg-slate-900 hover:bg-slate-800">{editingId ? "Save changes" : "Add charge rate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehousing add / edit dialog */}
      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="warehousing-dialog">
          <DialogHeader>
            <DialogTitle>{whEditingId ? "Edit warehousing rate" : "Add warehousing rate"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Category</Label>
              <Select value={whForm.label} onValueChange={(v) => setWhForm({ ...whForm, label: v })}>
                <SelectTrigger data-testid="wh-label" className="mt-1 rounded-sm border-slate-300"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                <SelectContent>
                  {WAREHOUSING_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Rate (£)</Label>
                <Input
                  data-testid="wh-rate"
                  type="number" step="0.01"
                  value={whForm.charge_rate}
                  onChange={(e) => setWhForm({ ...whForm, charge_rate: e.target.value })}
                  className="mt-1 rounded-sm border-slate-300"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Unit</Label>
                <Select value={whForm.unit} onValueChange={(v) => setWhForm({ ...whForm, unit: v })}>
                  <SelectTrigger data-testid="wh-unit" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WAREHOUSING_UNITS.map((u) => <SelectItem key={u.v} value={u.v}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Notes</Label>
              <Input
                data-testid="wh-notes"
                value={whForm.notes}
                onChange={(e) => setWhForm({ ...whForm, notes: e.target.value })}
                placeholder="e.g. depending on size, non-stackable, volume discounts available"
                className="mt-1 rounded-sm border-slate-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveWarehousing} data-testid="wh-save" className="rounded-sm bg-slate-900 hover:bg-slate-800">
              {whEditingId ? "Save changes" : "Add warehousing rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const WAREHOUSING_CATEGORIES = [
  "Empty Container",
  "Storage per Pallet",
  "Inbound",
  "Outbound",
  "Labour",
  "Picking",
];

const WAREHOUSING_UNITS = [
  { v: "per_pallet_per_week", label: "per pallet / week" },
  { v: "per_pallet",          label: "per pallet" },
  { v: "per_order",           label: "per order" },
  { v: "per_hour",            label: "per hour" },
  { v: "per_unit",            label: "per unit" },
  { v: "per_week",            label: "per week" },
  { v: "per_month",           label: "per month" },
  { v: "per_day",             label: "per day" },
  { v: "per_job",             label: "per job" },
];

function warehousingUnitLabel(v) {
  return (WAREHOUSING_UNITS.find((u) => u.v === v) || {}).label || v;
}
